import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Circle, Flex, Text, VStack, chakra } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    LuCalendar,
    LuEllipsis,
    LuFileText,
    LuHouse,
    LuPlus,
    LuWallet,
} from 'react-icons/lu';
import type { IconType } from 'react-icons';
import type { UserProfile } from '@/client';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { useActivityBadge } from '@/screens/activity/hooks/useActivityBadge';
import { AccountMenu } from './AccountMenu';
import { TAB_BAR_H, TAB_BAR_PB } from './layoutTokens';
import { sectionPath } from './navigation/navConfig';
import type { Workspace, WorkspaceNav } from './navigation/useWorkspaceNav';

interface MobileTabBarProps {
    nav: WorkspaceNav;
    user: UserProfile;
}

/**
 * The mobile navigation, replacing the hamburger drawer.
 *
 * A drawer hides the sections behind a tap and a wait; the bar keeps the three
 * that matter permanently in reach and puts the one thing members come to do —
 * add something — under the thumb, in the middle.
 *
 * Both stages get the middle button; what it offers differs. Manage adds to a
 * home you own — expense, calendar, document. Explore adds the only record a
 * search group has, a listing. Either way it is five slots, so `preferences`
 * gives up its tab in Explore exactly as it does in Manage.
 */
export function MobileTabBar({ nav, user }: MobileTabBarProps) {
    const intl = useIntl();
    const { workspace, sections, activeSectionId } = nav;
    const needsCount = useActivityBadge(
        workspace ? { type: workspace.kind === 'home' ? 'home' : 'group', id: workspace.id } : null,
    );

    const tabs = sections.filter((section) => section.tab);
    const overflow = sections.filter((section) => !section.tab);

    return (
        <Flex
            as="nav"
            aria-label={intl.formatMessage({
                id: 'e0f5b183-4d27-4c96-b8a0-1f7c3d5e9a26',
                defaultMessage: 'Main navigation',
            })}
            /* The last row of the shell, not an overlay on top of it. A fixed
               bar is laid out against the layout viewport while the shell is
               sized in `dvh`, so the two disagree by exactly the height of the
               browser's own toolbar: the bar drifted off the bottom of the
               shell and the scroll region had to carry a matching slab of dead
               padding to compensate. In flow it cannot drift, and the scroll
               region above it ends where the bar begins. `relative` so it still
               paints over that region — the FAB overhangs it. */
            position="relative"
            flexShrink={0}
            zIndex={20}
            minH={TAB_BAR_H}
            align="flex-start"
            justify="space-around"
            pt={2.5}
            px={2.5}
            pb={TAB_BAR_PB}
            bg="bg.panel"
            borderTopWidth="1px"
            borderColor="border.chrome"
        >
            {tabs.map((section) => (
                <RouterLink
                    key={section.id}
                    to={sectionPath(workspace?.stage ?? 'manage', workspace?.id ?? '', section)}
                    aria-current={activeSectionId === section.id ? 'page' : undefined}
                    style={{ minWidth: 60 }}
                >
                    <SlotBody
                        icon={section.icon}
                        label={intl.formatMessage(section.label)}
                        active={activeSectionId === section.id}
                        dot={section.id === 'activity' && needsCount !== undefined && needsCount > 0}
                    />
                </RouterLink>
            ))}

            {workspace && <AddSlot workspace={workspace} />}

            <MoreSlot
                nav={nav}
                user={user}
                overflow={overflow}
                active={overflow.some((section) => section.id === activeSectionId)}
            />
        </Flex>
    );
}

interface SlotBodyProps {
    icon: IconType;
    label: string;
    active: boolean;
    dot?: boolean;
}

function SlotBody({ icon: Icon, label, active, dot = false }: SlotBodyProps) {
    return (
        <VStack gap="5px" py="4px" minW="16">
            <Box position="relative" color={active ? 'gold.link' : 'fg.nav'} display="flex" aria-hidden>
                <Icon size={22} strokeWidth={active ? 1.8 : 1.6} />
                {dot && (
                    <Box
                        position="absolute"
                        top="-1px"
                        right="-3px"
                        w="2"
                        h="2"
                        borderRadius="full"
                        bg="gold.solid"
                        borderWidth="1.5px"
                        borderColor="bg.panel"
                    />
                )}
            </Box>
            <Text
                fontSize="10.5px"
                fontWeight={active ? 'bold' : 'semibold'}
                color={active ? 'gold.link' : 'fg.nav'}
                lineHeight="1.2"
            >
                {label}
            </Text>
        </VStack>
    );
}

/**
 * The one control the design puts in the middle: a 52px disc that overhangs the
 * bar, ringed in the bar's own colour so it reads as floating above it.
 *
 * Each item is an `?action=` deep link to the screen that owns the record, not a
 * form of its own — the expense drawer alone carries split methods, funding
 * sources and attachment linking, and a second one would drift from it.
 */
function AddSlot({ workspace }: { workspace: Workspace }) {
    const intl = useIntl();
    const navigate = useNavigate();

    const items =
        workspace.stage === 'manage'
            ? [
                  {
                      id: 'expense',
                      icon: LuWallet,
                      label: intl.formatMessage({
                          id: '9e0b4c73-1a58-4d26-b7f0-3c5e8a2d6f91',
                          defaultMessage: 'Add expense',
                      }),
                      to: `/home/${workspace.id}/expenses?action=submit-expense`,
                  },
                  {
                      id: 'calendar',
                      icon: LuCalendar,
                      label: intl.formatMessage({
                          id: '2d6f9a51-7c04-4b83-9e15-8a3b0d7c4e62',
                          defaultMessage: 'Add to calendar',
                      }),
                      to: `/home/${workspace.id}/schedule?action=add-event`,
                  },
                  {
                      id: 'document',
                      icon: LuFileText,
                      label: intl.formatMessage({
                          id: '5b1c7e08-3f92-4a46-8d70-6e2a9c4b1d35',
                          defaultMessage: 'Add document',
                      }),
                      to: `/home/${workspace.id}/documents?action=add-document`,
                  },
              ]
            : [
                  {
                      id: 'listing',
                      icon: LuHouse,
                      label: intl.formatMessage({
                          id: 'b49e541c-9cc2-43b5-a647-fabe6c45c211',
                          defaultMessage: 'Add listing',
                      }),
                      to: `/groups/${workspace.id}/listings/new`,
                  },
              ];

    return (
        <Box position="relative" minW="16" display="flex" flexDirection="column" alignItems="center">
            <MenuRoot positioning={{ placement: 'top' }}>
                <MenuTrigger asChild>
                    <chakra.button
                        type="button"
                        aria-label={intl.formatMessage({
                            id: '7a3e5c19-0b64-4d28-9f53-1c8a2e6d0b47',
                            defaultMessage: 'Add',
                        })}
                        mt="-22px"
                        borderWidth={0}
                        p={0}
                        cursor="pointer"
                        borderRadius="full"
                        bg="transparent"
                        transition="transform 0.18s ease"
                        _hover={{ transform: 'translateY(-2px)' }}
                        _motionReduce={{ transition: 'none' }}
                        _focusVisible={{ outline: '2px solid', outlineColor: 'gold.solid', outlineOffset: '3px' }}
                    >
                        {/* `lg` plus a ring in the bar's own colour: the ring is
                            what cuts the button out of the border it overhangs,
                            and a border does it with a stock shadow token
                            instead of a bespoke two-layer one. */}
                        <Circle
                            size="12"
                            bg="gold.fab"
                            color="fg"
                            boxShadow="lg"
                            borderWidth="1"
                            borderColor="bg.panel"
                        >
                            <LuPlus size={22} strokeWidth={2.1} />
                        </Circle>
                    </chakra.button>
                </MenuTrigger>
                <MenuContent minW="56" borderRadius="xl">
                    {items.map((item) => (
                        <MenuItem key={item.id} value={item.id} onClick={() => navigate(item.to)} cursor="button">
                            <Box color="gold.ink" display="flex" flexShrink={0}>
                                <item.icon size={18} strokeWidth={1.5} />
                            </Box>
                            <Text fontSize="14px" fontWeight="semibold" color="fg">
                                {item.label}
                            </Text>
                        </MenuItem>
                    ))}
                </MenuContent>
            </MenuRoot>

            <Text fontSize="10.5px" fontWeight="bold" color="fg" mt="5px" lineHeight="1.2">
                <FormattedMessage id="7a3e5c19-0b64-4d28-9f53-1c8a2e6d0b47" defaultMessage="Add" />
            </Text>
        </Box>
    );
}

interface MoreSlotProps {
    nav: WorkspaceNav;
    user: UserProfile;
    overflow: WorkspaceNav['sections'];
    active: boolean;
}

/**
 * The sections that did not get a slot, then the desktop account menu — mobile
 * has no sidebar card, so the workspace switcher lives here too.
 */
function MoreSlot({ nav, user, overflow, active }: MoreSlotProps) {
    const intl = useIntl();
    const navigate = useNavigate();
    const { workspace } = nav;

    const label = intl.formatMessage({
        id: '3c9e1a75-6b28-4f04-8d31-7e0b5c2a9d68',
        defaultMessage: 'More',
    });

    const sections = overflow.length > 0
        ? overflow.map((section) => (
            <MenuItem
                key={section.id}
                value={section.id}
                cursor="button"
                borderRadius="lg"
                onClick={() => navigate(sectionPath(workspace?.stage ?? 'manage', workspace?.id ?? '', section))}
            >
                <section.icon />
                {intl.formatMessage(section.label)}
            </MenuItem>
        ))
        : undefined;

    return (
        <AccountMenu
            user={user}
            workspace={workspace}
            homes={nav.homes}
            groups={nav.groups}
            sections={sections}
            positioning={{ placement: 'top-end' }}
            // Zag pins submenus to right-start; overlap lets them shift back on-screen over the parent.
            submenuPositioning={{ flip: false, overlap: true, overflowPadding: 12 }}
        >
            <chakra.button
                type="button"
                aria-label={label}
                bg="transparent"
                borderWidth={0}
                p={0}
                minW="16"
                cursor="pointer"
                borderRadius="lg"
                _focusVisible={{ outline: '2px solid', outlineColor: 'gold.solid', outlineOffset: '2px' }}
            >
                <SlotBody icon={LuEllipsis} label={label} active={active} />
            </chakra.button>
        </AccountMenu>
    );
}
